import { trackError } from '@billy/track-error'
import { useCallback, useRef, useState } from 'react'
import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { useQueryClient } from 'react-query'

import { QueryKeys } from '../../../../../enums/queryKeys'
import type { FecImportItem } from '../query-api'
import { appendUniqueIds } from '../utils/appendUniqueIds'
import { isFecImportAnalysisFailed } from '../utils/isFecImportAnalysisFailed'
import { isFecImportRetryable } from '../utils/isFecImportRetryable'
import { isPartialDeleteError, useDeleteFecImport } from './useDeleteFecImport'
import { useRetryFecImports } from './useRetryFecImports'

interface UseFecImportSessionActionsParams {
  hasExplicitSessionImportIdsRef: MutableRefObject<boolean>
  isRefetchError: boolean
  markImportsRetried: (imports: Array<Pick<FecImportItem, 'id' | 'updatedTime'>>) => void
  refetch: () => Promise<unknown>
  resetReportedAnalysisErrors: () => void
  sessionFecImports: FecImportItem[]
  sessionImportIds: string[]
  setHasEnteredAnalysingView: Dispatch<SetStateAction<boolean>>
  setIgnoreOrgInProgressImports: Dispatch<SetStateAction<boolean>>
  setSessionFiles: Dispatch<SetStateAction<File[]>>
  setSessionImportIds: Dispatch<SetStateAction<string[]>>
}

const trackSessionActionError = (action: string, error: unknown): void => {
  trackError(new Error(`FEC import ${action} failed: ${error instanceof Error ? error.message : String(error)}`))
}

const pruneDeletedSessionIds = (
  setSessionImportIds: Dispatch<SetStateAction<string[]>>,
  deletedIds: string[],
): void => {
  setSessionImportIds((currentIds) => currentIds.filter((importId) => !deletedIds.includes(importId)))
}

export const useFecImportSessionActions = ({
  hasExplicitSessionImportIdsRef,
  isRefetchError,
  markImportsRetried,
  refetch,
  resetReportedAnalysisErrors,
  sessionFecImports,
  sessionImportIds,
  setHasEnteredAnalysingView,
  setIgnoreOrgInProgressImports,
  setSessionFiles,
  setSessionImportIds,
}: UseFecImportSessionActionsParams) => {
  const [isReuploading, setIsReuploading] = useState(false)
  const [isRetryingAnalysis, setIsRetryingAnalysis] = useState(false)
  const [cancellingFecImportIds, setCancellingFecImportIds] = useState<string[]>([])
  const cancellingFecImportIdsRef = useRef<string[]>([])
  const queryClient = useQueryClient()
  const { deleteMany } = useDeleteFecImport()
  const retryMany = useRetryFecImports()

  const invalidateFecImports = useCallback(() => queryClient.invalidateQueries([QueryKeys.FecImports]), [queryClient])

  const onReupload = useCallback(async () => {
    setIsReuploading(true)

    try {
      await deleteMany(sessionImportIds)
      setSessionFiles([])
      setSessionImportIds([])
      setHasEnteredAnalysingView(false)
      hasExplicitSessionImportIdsRef.current = false
      // Whatever else is running on the server is not part of this upload: start from the dropzone, as after a cancel.
      setIgnoreOrgInProgressImports(true)
    } catch (error) {
      if (isPartialDeleteError(error)) {
        pruneDeletedSessionIds(setSessionImportIds, error.deletedIds)
      }

      trackSessionActionError('reupload delete', error)
    } finally {
      setIsReuploading(false)
      await invalidateFecImports()
    }
  }, [
    deleteMany,
    hasExplicitSessionImportIdsRef,
    invalidateFecImports,
    sessionImportIds,
    setHasEnteredAnalysingView,
    setIgnoreOrgInProgressImports,
    setSessionFiles,
    setSessionImportIds,
  ])

  const onRetryAnalysis = useCallback(async () => {
    const retryableFecImports = sessionFecImports.filter(isFecImportRetryable)
    const retryableFecImportIds = retryableFecImports.map((fecImport) => fecImport.id)

    // Nothing the backend can retry: a lost connection, or a wait that outlasted the client-side limit. Look again.
    if (isRefetchError || retryableFecImportIds.length === 0) {
      // Show the retrying state until the list answers, so the click is not silent while the server is still working.
      setIsRetryingAnalysis(true)

      try {
        await refetch()
      } finally {
        setIsRetryingAnalysis(false)
      }

      return
    }

    setIsRetryingAnalysis(true)

    try {
      resetReportedAnalysisErrors()

      const { failures, retriedIds } = await retryMany(retryableFecImportIds)

      // The retry response (and the next list) can still describe the old failure, because the backend only
      // queues the work. Keep showing these imports as running until the server reports a change.
      markImportsRetried(retryableFecImports.filter((fecImport) => retriedIds.includes(fecImport.id)))

      if (failures.length > 0) {
        trackSessionActionError(
          'retry analysis',
          new Error(
            failures
              .map(
                ({ fecImportId, reason }) =>
                  `${fecImportId}: ${reason instanceof Error ? reason.message : String(reason)}`,
              )
              .join('; '),
          ),
        )
      }
    } catch (error) {
      trackSessionActionError('retry analysis', error)
    } finally {
      setIsRetryingAnalysis(false)
      await invalidateFecImports()
    }
  }, [
    invalidateFecImports,
    isRefetchError,
    markImportsRetried,
    refetch,
    resetReportedAnalysisErrors,
    retryMany,
    sessionFecImports,
  ])

  const onCancelAnalysingFile = useCallback(
    async (fecImportId: string) => {
      if (cancellingFecImportIdsRef.current.includes(fecImportId)) {
        return
      }

      cancellingFecImportIdsRef.current = appendUniqueIds(cancellingFecImportIdsRef.current, [fecImportId])
      setCancellingFecImportIds(cancellingFecImportIdsRef.current)

      try {
        await deleteMany([fecImportId])

        const cancelledFileName = sessionFecImports.find((fecImport) => fecImport.id === fecImportId)?.fileName

        setSessionImportIds((currentIds) => {
          const nextIds = currentIds.filter((importId) => importId !== fecImportId)

          if (nextIds.length === 0) {
            setHasEnteredAnalysingView(false)
            hasExplicitSessionImportIdsRef.current = false
            setIgnoreOrgInProgressImports(true)
          }

          return nextIds
        })

        if (cancelledFileName) {
          setSessionFiles((currentFiles) => currentFiles.filter((file) => file.name !== cancelledFileName))
        }
      } catch (error) {
        trackSessionActionError('cancel analysing file', error)
        throw error
      } finally {
        cancellingFecImportIdsRef.current = cancellingFecImportIdsRef.current.filter(
          (cancellingId) => cancellingId !== fecImportId,
        )
        setCancellingFecImportIds(cancellingFecImportIdsRef.current)
        await invalidateFecImports()
      }
    },
    [
      deleteMany,
      hasExplicitSessionImportIdsRef,
      invalidateFecImports,
      sessionFecImports,
      setHasEnteredAnalysingView,
      setIgnoreOrgInProgressImports,
      setSessionFiles,
      setSessionImportIds,
    ],
  )

  const onSkipFailedFileContinue = useCallback(async () => {
    const failedImportIds = sessionFecImports
      .filter((fecImport) => isFecImportAnalysisFailed(fecImport))
      .map((fecImport) => fecImport.id)

    if (failedImportIds.length === 0) {
      return
    }

    setIsReuploading(true)

    try {
      await deleteMany(failedImportIds)
      setSessionImportIds((currentIds) => currentIds.filter((importId) => !failedImportIds.includes(importId)))
    } catch (error) {
      const canContinueAfterPartialDelete = isPartialDeleteError(error) && error.deletedIds.length > 0

      if (isPartialDeleteError(error)) {
        pruneDeletedSessionIds(setSessionImportIds, error.deletedIds)
      }

      trackSessionActionError('skip failed file delete', error)

      if (!canContinueAfterPartialDelete) {
        throw error
      }
    } finally {
      setIsReuploading(false)
      await invalidateFecImports()
    }
  }, [deleteMany, invalidateFecImports, sessionFecImports, setSessionImportIds])

  return {
    cancellingFecImportIds,
    isReuploading,
    isRetryingAnalysis,
    onCancelAnalysingFile,
    onRetryAnalysis,
    onReupload,
    onSkipFailedFileContinue,
  }
}
